import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useState } from "react";

import { queryKey } from "../../../api/endpoints";
import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import { useCurrentMe } from "../../../app/auth";
import type { MemoryEdit } from "./memoryEdits";

export type MemoryOwner = "member" | "admin" | "none";

/**
 * The person's memory, from the right place: a member of a discipline has it in that discipline (and it does not travel
 * to another one); the admin, who has no discipline, has the admin's own memory. A person who has not chosen a
 * discipline yet has none to show.
 */
export function useMemory() {
  const me = useCurrentMe();
  const queryClient = useQueryClient();
  const owner: MemoryOwner = me.is_admin && !me.active ? "admin" : me.active ? "member" : "none";

  const memberQuery = useApiQuery("my_memory", {}, { enabled: owner === "member" });
  const adminQuery = useApiQuery("admin_memory", {}, { enabled: owner === "admin" });
  const editMember = useApiCommand("edit_my_memory");
  const editAdmin = useApiCommand("edit_admin_memory");
  const clearMember = useApiCommand("clear_my_memory");
  const [problem, setProblem] = useState<string | null>(null);

  const query = owner === "admin" ? adminQuery : memberQuery;
  const saving = editMember.isPending || editAdmin.isPending || clearMember.isPending;

  const edit = async (change: MemoryEdit): Promise<boolean> => {
    setProblem(null);
    try {
      if (owner === "admin") {
        const reply = await editAdmin.mutateAsync({ set: change.set, remove: change.remove });
        queryClient.setQueryData(queryKey("admin_memory", {}) as unknown as QueryKey, reply.data);
      } else {
        const reply = await editMember.mutateAsync({ set: change.set, remove: change.remove });
        queryClient.setQueryData(queryKey("my_memory", {}) as unknown as QueryKey, reply.data);
      }
      return true;
    } catch (error) {
      setProblem(refusalText(error));
      return false;
    }
  };

  /** Clear everything the app remembers. The admin's memory has no such command. */
  const clear = async (): Promise<boolean> => {
    if (owner !== "member") return false;
    setProblem(null);
    try {
      const reply = await clearMember.mutateAsync({});
      queryClient.setQueryData(queryKey("my_memory", {}) as unknown as QueryKey, reply.data);
      return true;
    } catch (error) {
      setProblem(refusalText(error));
      return false;
    }
  };

  return {
    owner,
    memory: query.data?.memory,
    loading: query.isPending && owner !== "none",
    loadError: query.isError ? refusalText(query.error) : null,
    reload: () => void query.refetch(),
    edit,
    clear,
    canClear: owner === "member",
    saving,
    problem,
  };
}
